import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale } from 'react-datepicker'
import { enUS, tr, sv } from 'date-fns/locale'
import 'react-datepicker/dist/react-datepicker.css'
import './LocalizedDateTimeInput.css'

const LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  tr,
  sv,
}

type Locale = typeof enUS

function getLocale(lng: string): Locale {
  const code = lng?.split('-')[0] || 'en'
  return LOCALE_MAP[code] ?? enUS
}

// Register locales for react-datepicker (uses string keys)
registerLocale('en', enUS)
registerLocale('tr', tr)
registerLocale('sv', sv)

export interface LocalizedDateTimeInputProps {
  value: string
  onChange: (value: string) => void
  required?: boolean
  className?: string
  placeholder?: string
  disabled?: boolean
}

export default function LocalizedDateTimeInput({
  value,
  onChange,
  required,
  className = '',
  placeholder,
  disabled = false,
}: LocalizedDateTimeInputProps) {
  const { i18n } = useTranslation()
  const locale = getLocale(i18n.language)

  const dateValue = value ? new Date(value) : null

  const handleChange = (date: Date | null) => {
    if (!date) {
      onChange('')
      return
    }
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const h = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    onChange(`${y}-${m}-${d}T${h}:${min}`)
  }

  const todayLabel = i18n.t('common:datePicker.today', 'Today')
  const clearLabel = i18n.t('common:datePicker.clear', 'Clear')

  return (
    <div className="localized-datetime-input">
    <DatePicker
      selected={dateValue}
      onChange={handleChange}
      showTimeSelect
      timeFormat="HH:mm"
      timeIntervals={15}
      dateFormat="PPp"
      locale={locale}
      required={required}
      className={className}
      placeholderText={placeholder}
      isClearable={!required && !disabled}
      clearButtonTitle={clearLabel}
      todayButton={todayLabel}
      popperPlacement="bottom-start"
      popperClassName="localized-datepicker-popper"
      disabled={disabled}
    />
    </div>
  )
}
